/**
 * What Syft will accept as an upload, decided before anything parses it.
 *
 * This runs in two places on purpose: in the browser, so a student learns
 * their file is too big before waiting through an upload, and on the server,
 * where it is the check that actually counts. The browser copy is a
 * courtesy; anyone can POST straight past it.
 */

/** 25MB, matching the bound the storage schema enforces. */
export const MAX_PDF_BYTES = 25 * 1024 * 1024;

/**
 * A page ceiling, because page count is the cheapest lever for making a
 * small file expensive: a PDF of a few hundred kilobytes can declare tens of
 * thousands of pages and tie the browser up rendering them.
 */
export const MAX_PDF_PAGES = 2_000;

/**
 * The PDF header. The spec allows leading junk before it, so it is searched
 * for near the start rather than required at byte zero.
 */
const PDF_MAGIC = "%PDF-";
const MAGIC_WINDOW = 1024;

export type FileVerdict = { ok: true } | { ok: false; error: string };

export function describeBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

/** Size only. Cheap, and the one worth doing before reading the file at all. */
export function checkSize(bytes: number): FileVerdict {
  if (bytes <= 0) return { ok: false, error: "That file is empty." };
  if (bytes > MAX_PDF_BYTES) {
    return {
      ok: false,
      error: `That PDF is ${describeBytes(bytes)}. The limit is ${describeBytes(MAX_PDF_BYTES)}.`,
    };
  }
  return { ok: true };
}

/**
 * Is this actually a PDF?
 *
 * By its bytes, never by its name or its declared MIME type. Both of those
 * come from whoever is uploading, so a .pdf extension on an HTML file is a
 * claim, not evidence — and believing that claim is how a stored file ends
 * up being served back as something the browser will execute.
 */
export function looksLikePdf(head: Uint8Array): boolean {
  const window = head.subarray(0, MAGIC_WINDOW);
  const text = new TextDecoder("latin1").decode(window);
  return text.includes(PDF_MAGIC);
}

/** Size and magic bytes together, for a buffer already in hand. */
export function checkPdfBytes(bytes: Uint8Array): FileVerdict {
  const size = checkSize(bytes.byteLength);
  if (!size.ok) return size;

  if (!looksLikePdf(bytes)) {
    return {
      ok: false,
      error: "That file is not a PDF, whatever it is named. Upload a PDF, or paste the text.",
    };
  }
  return { ok: true };
}

/**
 * Whether the extracted text is enough to rebuild a reading from.
 *
 * Separate from "did extraction fail", because the awkward case is a PDF
 * that yields a little text and no more: a title page that scanned as text
 * in front of forty pages of images. Extraction reports success, and without
 * this the student only finds out at submit, with a message about pasting
 * more text that makes no sense for a file they just attached.
 */
export const MIN_USEFUL_TEXT = 50;

export function checkExtractedText(text: string, pageCount: number): FileVerdict {
  if (text.trim().length >= MIN_USEFUL_TEXT) return { ok: true };

  return {
    ok: false,
    error:
      pageCount > 1
        ? `Only a little text could be read from these ${pageCount} pages, so this is probably a scan. Paste the text instead.`
        : "Almost no text could be read from this PDF, so it is probably a scan. Paste the text instead.",
  };
}
